import React from 'react';
import PropTypes from 'prop-types';

import {LineChart} from '@mui/x-charts';

import {Alert, Box, Paper, Typography} from 'views/components';
import {Translate, translate} from 'views/module';

import ChartLegend from '../shared/ChartLegend';
import SectionHeading from '../shared/SectionHeading';
import PanelSkeleton from '../shared/skeletons/PanelSkeleton';

import formatters from '../shared/formatters';

const chartSx = {
  '& .MuiChartsAxis-line, & .MuiChartsAxis-tick': {
    stroke: '#CEC8BD',
  },
  '& .MuiChartsAxis-tickLabel': {
    fill: '#7B7786',
    fontSize: 11,
  },
  '& .MuiChartsGrid-line': {
    stroke: '#EEE8DD',
    strokeDasharray: '4 4',
  },
};

const MONEY_SERIES = [
  {key: 'revenue', color: '#2E7D32', labelExpression: 'metrics.ClientMetrics.Forecast.Revenue'},
  {key: 'laborCost', color: '#DA7955', labelExpression: 'metrics.ClientMetrics.Forecast.Labor'},
  {key: 'grossProfit', color: '#243B63', labelExpression: 'metrics.ClientMetrics.Forecast.GrossProfit'},
];

const CAPACITY_SERIES = [
  {key: 'scheduledHours', color: '#5B8DB8', axis: 'hours', labelExpression: 'metrics.ClientMetrics.Forecast.ScheduledHours'},
  {key: 'weightedHours', color: '#8D84AB', axis: 'hours', labelExpression: 'metrics.ClientMetrics.Forecast.WeightedHours'},
  {key: 'activeClientCount', color: '#2E7D32', axis: 'counts', labelExpression: 'metrics.ClientMetrics.Forecast.ScheduledClients'},
  {key: 'caregiverCount', color: '#C58B3B', axis: 'counts', labelExpression: 'metrics.ClientMetrics.Forecast.Caregivers'},
];

const ForecastSection = (props) => {
  const {
    isLoading,
    currentSnapshot,
  } = props;

  const {xAxisLabels, moneySeries, capacitySeries, legendItems} = React.useMemo(() => {
    const weeks = currentSnapshot?.forecast?.weeks || {};
    const weekKeys = formatters.sortWeekKeysAscending(weeks);
    const buildSeries = (seriesConfigs) => {
      return seriesConfigs.map((seriesConfig) => ({
        id: seriesConfig.key,
        label: translate(seriesConfig.labelExpression),
        color: seriesConfig.color,
        data: weekKeys.map((weekKey) => weeks[weekKey]?.[seriesConfig.key] ?? null),
        showMark: false,
        connectNulls: false,
        ...(seriesConfig.axis ? {yAxisId: seriesConfig.axis} : {}),
      }));
    };
    const buildLegend = (seriesConfigs) => {
      return seriesConfigs.map((seriesConfig) => ({
        key: seriesConfig.key,
        label: translate(seriesConfig.labelExpression),
        color: seriesConfig.color,
      }));
    };

    return {
      xAxisLabels: weekKeys.map((weekKey) => formatters.formatWeekLabel(weekKey)),
      moneySeries: buildSeries(MONEY_SERIES),
      capacitySeries: buildSeries(CAPACITY_SERIES),
      legendItems: {
        money: buildLegend(MONEY_SERIES),
        capacity: buildLegend(CAPACITY_SERIES),
      },
    };
  }, [currentSnapshot]);

  const renderChartPanel = (titleExpression, subtitleExpression, chart, legend) => (
    <Paper
      sx={{
        p: 3,
        borderRadius: '12px',
        border: '1px solid #E5E5E5',
        boxShadow: 'none',
        minWidth: 0,
      }}
    >
      <Typography
        sx={{
          fontSize: '16px',
          fontWeight: 600,
          lineHeight: '21px',
          color: '#1A1916',
          mb: 0.25,
        }}
      >
        <Translate expression={titleExpression} />
      </Typography>
      <Typography
        sx={{
          fontSize: '12px',
          lineHeight: '16px',
          color: '#A8A49E',
          mb: 2,
        }}
      >
        <Translate expression={subtitleExpression} />
      </Typography>
      <Box
        aria-label={translate(titleExpression)}
        sx={{
          border: '1px solid #EBE6DE',
          borderRadius: 3,
          backgroundColor: '#FFFDFC',
          px: 1,
          pt: 1.5,
          pb: 0.5,
        }}
      >
        {chart}
        <ChartLegend items={legend} />
      </Box>
    </Paper>
  );

  return (
    <Box>
      <SectionHeading expression='metrics.ClientMetrics.Forecast.SectionTitle' />
      {
        isLoading &&
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {xs: '1fr', xl: 'repeat(2, minmax(0, 1fr))'},
            gap: 2,
          }}
        >
          <PanelSkeleton height={300} />
          <PanelSkeleton height={300} />
        </Box>
      }
      {
        !isLoading &&
        !xAxisLabels.length &&
        <Alert severity='info'>
          <Translate expression='metrics.ClientMetrics.EmptyStates.NoForecastWeeks' />
        </Alert>
      }
      {
        !isLoading &&
        xAxisLabels.length > 0 &&
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {xs: '1fr', xl: 'repeat(2, minmax(0, 1fr))'},
            gap: 2,
          }}
        >
          {
            renderChartPanel(
                'metrics.ClientMetrics.Forecast.FinancialTitle',
                'metrics.ClientMetrics.Forecast.FinancialSubtitle',
                <LineChart
                  height={300}
                  margin={{top: 12, right: 16, bottom: 28, left: 60}}
                  grid={{horizontal: true}}
                  slotProps={{legend: {hidden: true}}}
                  xAxis={[{scaleType: 'point', data: xAxisLabels}]}
                  series={moneySeries}
                  sx={chartSx}
                />,
                legendItems.money,
            )
          }
          {
            renderChartPanel(
                'metrics.ClientMetrics.Forecast.CapacityTitle',
                'metrics.ClientMetrics.Forecast.CapacitySubtitle',
                <LineChart
                  height={300}
                  margin={{top: 12, right: 48, bottom: 28, left: 60}}
                  grid={{horizontal: true}}
                  slotProps={{legend: {hidden: true}}}
                  xAxis={[{scaleType: 'point', data: xAxisLabels}]}
                  yAxis={[{id: 'hours', min: 0}, {id: 'counts', min: 0}]}
                  leftAxis='hours'
                  rightAxis='counts'
                  series={capacitySeries}
                  sx={chartSx}
                />,
                legendItems.capacity,
            )
          }
        </Box>
      }
    </Box>
  );
};

ForecastSection.propTypes = {
  isLoading: PropTypes.bool.isRequired,
  currentSnapshot: PropTypes.object,
};

export default React.memo(ForecastSection);
